<template>
    <transition name="fade">
        <ds-input-field
            v-if="checkTypeAndDisplayField(customField, contactFieldTypes.CUSTOM_FIELD_NAMES.CURRENCY)"
            ref="currency"
            v-model.trim="customField.fieldValue"
            data-qa="customField_CURRENCY"
            class="input-field"
            type="number"
            :step="0.01"
            :label="customField.label"
            :name="`custom-field-${customField.id}`"
            :intercept-on-update="interceptInput(customField, `currency`)"
            :submitted="submitted"
        />

        <ds-date-picker
            v-if="checkTypeAndDisplayField(customField, contactFieldTypes.CUSTOM_FIELD_NAMES.DATE)"
            v-model.trim="customField.fieldValue"
            data-qa="customField_DATE"
            class="input-field"
            :label="customField.label"
            :name="`custom-field-${customField.id}`"
            :submitted="submitted"
            date-only
        />

        <ds-date-picker
            v-if="checkTypeAndDisplayField(customField, contactFieldTypes.CUSTOM_FIELD_NAMES.DATETIME)"
            ref="dateTime$"
            v-model="customField.fieldValue"
            data-qa="customField_DATETIME"
            class="input-field"
            time
            :name="`custom-field-${customField.id}`"
            :label="customField.label"
            :submitted="submitted"
            :intercept-on-update="interceptInput(customField, `dateTime`)"
        />

        <ds-select-field
            v-if="checkTypeAndDisplayField(customField, contactFieldTypes.CUSTOM_FIELD_NAMES.DAY_OF_WEEK)"
            v-model="customField.fieldValue"
            data-qa="customField_DAY_OF_WEEK"
            class="input-field"
            bind-value-only
            value-prop="value"
            :label="customField.label"
            :name="`custom-field-${customField.id}`"
            :options="formatOptions(customField)"
            :submitted="submitted"
        />

        <ds-input-field
            v-if="checkTypeAndDisplayField(customField, contactFieldTypes.CUSTOM_FIELD_NAMES.DECIMAL_NUMBER)"
            ref="decimalNumber"
            v-model.trim="customField.fieldValue"
            data-qa="customField_DECIMAL_NUMBER"
            class="input-field"
            :label="customField.label"
            :name="`custom-field-${customField.id}`"
            :submitted="submitted"
            :intercept-on-update="interceptInput(customField, `decimalNumber$`)"
        />

        <ds-multiselect
            v-if="checkTypeAndDisplayField(customField, contactFieldTypes.CUSTOM_FIELD_NAMES.DROPDOWN)"
            v-model="customField.fieldValue"
            data-qa="customField_DROPDOWN"
            class="input-field"
            bind-value-only
            show-blank
            :label="customField.label"
            :placeholder="customField.label"
            :name="`custom-field-${customField.id}`"
            :options="formatOptions(customField)"
            :submitted="submitted"
        />

        <ds-input-field
            v-if="checkTypeAndDisplayField(customField, contactFieldTypes.CUSTOM_FIELD_NAMES.SSN)"
            v-model="customField.fieldValue"
            data-qa="customField_SSN"
            class="input-field"
            type="text"
            :label="customField.label"
            :name="`custom-field-${customField.id}`"
            :submitted="submitted"
        />

        <ds-multiselect
            v-if="checkTypeAndDisplayField(customField, contactFieldTypes.CUSTOM_FIELD_NAMES.DRILLDOWN)"
            v-model="customField.fieldValue"
            data-qa="customField_DRILLDOWN"
            class="input-field"
            bind-value-only
            :placeholder="customField.label"
            :label="customField.label"
            :name="`custom-field-${customField.id}`"
            :options="formatDrillDownOptions(customField)"
            :submitted="submitted"
        />

        <ds-input-field
            v-if="checkTypeAndDisplayField(customField, contactFieldTypes.CUSTOM_FIELD_NAMES.EMAIL)"
            v-model.trim="customField.fieldValue"
            data-qa="customField_EMAIL"
            class="input-field"
            type="email"
            :label="customField.label"
            :name="`custom-field-${customField.id}`"
            :submitted="submitted"
        >
            <ds-icon-button
                slot="trailing"
                class="trailing-icon"
                @click="sendEmail(customField.fieldValue)"
            >
                <ds-icon
                    v-if="fieldEmptyCheck(customField.fieldValue)"
                    class="external-look"
                    name="send"
                />
            </ds-icon-button>
        </ds-input-field>

        <ds-select-field
            v-if="checkTypeAndDisplayField(customField, contactFieldTypes.CUSTOM_FIELD_NAMES.MONTH)"
            v-model="customField.fieldValue"
            data-qa="customField_MONTH"
            class="input-field"
            label-prop="label"
            value-prop="value"
            bind-value-only
            searchable
            :label="customField.label"
            :name="`custom-field-${customField.id}`"
            :options="formatOptions(customField)"
            :submitted="submitted"
        />

        <ds-input-field
            v-if="checkTypeAndDisplayField(customField, contactFieldTypes.CUSTOM_FIELD_NAMES.NAME)"
            v-model="customField.fieldValue"
            data-qa="customField_NAME"
            class="input-field"
            type="text"
            :label="customField.label"
            :name="`custom-field-${customField.id}`"
            :submitted="submitted"
        />

        <ds-input-field
            v-if="checkTypeAndDisplayField(customField, contactFieldTypes.CUSTOM_FIELD_NAMES.PERCENT)"
            ref="percent"
            v-model.trim="customField.fieldValue"
            data-qa="customField_PERCENT"
            class="input-field"
            type="number"
            :label="customField.label"
            :name="`custom-field-${customField.id}`"
            :submitted="submitted"
            :intercept-on-update="interceptInput(customField, `percent`)"
        />

        <ds-input-field
            v-if="checkTypeAndDisplayField(customField, contactFieldTypes.CUSTOM_FIELD_NAMES.PHONE_NUMBER)"
            ref="phone"
            v-model.trim="customField.fieldValue"
            data-qa="customField_PHONE_NUMBER"
            class="input-field"
            type="tel"
            :label="customField.label"
            :name="`custom-field-${customField.id}`"
            :submitted="submitted"
            :intercept-on-update="interceptInput(customField, `phone`)"
        />

        <div v-if="checkTypeAndDisplayField(customField, contactFieldTypes.CUSTOM_FIELD_NAMES.RADIO)" class="radio-group-container">
            <small>{{ customField.label }}</small>
            <ds-radio-group
                v-model="customField.fieldValue"
                data-qa="customField_RADIO"
                class="input-field"
                :options="formatOptions(customField)"
                :submitted="submitted"
            />
        </div>

        <ds-select-field
            v-if="checkTypeAndDisplayField(customField, contactFieldTypes.CUSTOM_FIELD_NAMES.STATE)"
            v-model="customField.fieldValue"
            data-qa="customField_STATE"
            class="input-field"
            label-prop="label"
            value-prop="value"
            bind-value-only
            :label="customField.label"
            :name="`custom-field-${customField.id}`"
            :options="formatOptions(customField)"
            :submitted="submitted"
        />

        <ds-input-field
            v-if="checkTypeAndDisplayField(customField, contactFieldTypes.CUSTOM_FIELD_NAMES.TEXT)"
            v-model="customField.fieldValue"
            data-qa="customField_TEXT"
            class="input-field"
            type="text"
            :label="customField.label"
            :name="`custom-field-${customField.id}`"
            :submitted="submitted"
        />

        <ds-text-area-field
            v-if="checkTypeAndDisplayField(customField, contactFieldTypes.CUSTOM_FIELD_NAMES.TEXT_AREA)"
            v-model.trim="customField.fieldValue"
            data-qa="customField_TEXT_AREA"
            type="text"
            class="text-area-format input-field"
            :maxlength="65000"
            :label="customField.label"
            :name="`custom-field-${customField.id}`"
            :submitted="submitted"
        />

        <ds-input-field
            v-if="checkTypeAndDisplayField(customField, contactFieldTypes.CUSTOM_FIELD_NAMES.WEBSITE)"
            ref="website"
            v-model.trim="customField.fieldValue"
            data-qa="customField_WEBSITE"
            class="input-field"
            type="url"
            :label="customField.label"
            :name="`custom-field-${customField.id}`"
            :submitted="submitted"
            :intercept-on-update="interceptInput(customField, `website`)"
        >
            <ds-icon-button
                slot="trailing"
                class="trailing-icon"
                @click="webSite(customField.fieldValue)"
            >
                <ds-icon
                    v-if="fieldEmptyCheck(customField.fieldValue)"
                    class="external-look"
                    name="external-link"
                />
            </ds-icon-button>
        </ds-input-field>

        <ds-input-field
            v-if="checkTypeAndDisplayField(customField, contactFieldTypes.CUSTOM_FIELD_NAMES.WHOLE_NUMBER)"
            v-model.trim="customField.fieldValue"
            data-qa="customField_WHOLE_NUMBER"
            class="input-field"
            type="number"
            :label="customField.label"
            :name="`custom-field-${customField.id}`"
            :submitted="submitted"
        />

        <ds-input-field
            v-if="checkTypeAndDisplayField(customField, contactFieldTypes.CUSTOM_FIELD_NAMES.YEAR)"
            v-model.trim="customField.fieldValue"
            data-qa="customField_YEAR"
            class="input-field"
            type="number"
            :label="customField.label"
            :name="`custom-field-${customField.id}`"
            :submitted="submitted"
        />


        <ds-select-field
            v-if="checkTypeAndDisplayField(customField, contactFieldTypes.CUSTOM_FIELD_NAMES.YES_NO)"
            v-model="customField.fieldValue"
            data-qa="customField_YES_NO"
            class="input-field"
            label-prop="label"
            value-prop="value"
            bind-value-only
            allow-null
            :label="customField.label"
            :name="`custom-field-${customField.id}`"
            :options="formatYesNoOptions(customField)"
            :submitted="submitted"
        />

        <ds-multiselect
            v-if="checkTypeAndDisplayField(customField, contactFieldTypes.CUSTOM_FIELD_NAMES.MULTISELECT)"
            v-model="customField.fieldValue"
            data-qa="customField_MULTISELECT"
            class="input-field"
            bind-value-only
            multiple
            searchable
            :label="customField.label"
            :placeholder="customField.label"
            :options="formatOptions(customField)"
        />

        <ds-select-field
            v-if="checkTypeAndDisplayField(customField, contactFieldTypes.CUSTOM_FIELD_NAMES.USER)"
            v-model="customField.fieldValue"
            data-qa="customField_USER"
            bind-value-only
            class="input-field"
            :label="customField.label"
            :name="`custom-field-${customField.id}`"
            :options="formatOptions(customField)"
            :submitted="submitted"
        />

        <ds-multiselect
            v-if="checkTypeAndDisplayField(customField, contactFieldTypes.CUSTOM_FIELD_NAMES.USER_MULTISELECT)"
            v-model="customField.fieldValue"
            data-qa="customField_USER_MULTISELECT"
            class="input-field"
            bind-value-only
            multiple
            searchable
            :label="customField.label"
            :placeholder="customField.label"
            :options="formatOptions(customField)"
        />
    </transition>
</template>

<script>
import axios from 'axios';
import moment from 'moment-timezone';
import contactFieldTypes from '../contactFieldTypes';

export default {
    name: 'ContactLayoutCustomFieldDisplay',

    props: {
        customFields: Object,
        id: [String, Number],
        headerDetails: Object,
        searchCriteria: String,
        headerDisplay: Object,
    },

    data() {
        return {
            serverTimeZone: '',
            contactFieldTypes,
            customTab: Object,
            customField: {},
            submitted: false,
            customTabId: this.tabId,
            customTabMap: new Map(),
            customTabMapCopy: new Map(),
            customFieldsCopy: {},
            isCustomFieldChanged: false,
            customWatcherMap: new Map(),
            yesNo: [
                { label: 'No', value: 0 },
                { label: 'Yes', value: 1 },
            ],
            yesNoSelected: [
                { label: 'Not selected', value: null },
                { label: 'No', value: 0 },
                { label: 'Yes', value: 1 },
            ],
        };
    },

    created() {
        this.customField = this.customFields;
    },

    computed: {
        concatHeader() {
            // appending header with id because to make header unique
            return (head, id) => {
                return `${head?.replace(/\s+/g, '').toLowerCase()}_${id}`;
            };
        },

        checkField() {
            return (data) => {
                const searchField = this.searchCriteria?.replace(/\s+/g, '');

                Object.keys(this.headerDisplay)?.forEach((key) => {
                    this.headerDisplay[key] = key.split('_')[0].toLowerCase().includes(searchField.toLowerCase());

                    return this.headerDisplay;
                });

                return data?.replace(/\s+/g, '').toLowerCase().includes(searchField?.toLowerCase());
            };
        },

        displayField() {
            // searching with field names should return true if exist
            return (field) => {
                field = field?.replace(/\s+/g, '').toLowerCase();
                const header = this.concatHeader(this.headerDetails?.name, this.headerDetails?.id);

                return this.checkField(field) || this.headerDisplay[header];
            };
        },

        checkTypeAndDisplayField() {
            return (field, type) => {
                return field?.fieldType === type && this.displayField(field?.label);
            };
        },
    },

    methods: {
        webSite(address) {
            window.open(address.toString());
        },

        fieldEmptyCheck(value) {
            return !(value === '' || value === null);
        },

        async sendEmail(emailAddress) {
            try {
                const response = await axios.get('/app/contact/emailId');

                const email = response.data;

                window.open(`/Mail/compose.jsp?id=${email.mailId}&templateid=${email.templateId}&Mail0ToAddress_text=${emailAddress}&mergeid=${this.id}`);
            } catch (error) {
                this.$error({ message: 'error while sending email' });
            }
        },

        interceptInput(field, ref) {
            if (field && field.fieldValue && field.fieldType === 'Website') {
                const hasPrefix = field.fieldValue.includes('http') || field.fieldValue.includes('https');
                const hasWww = field.fieldValue.includes('www.');
                const isDomainOnly = field.fieldValue.includes('.') && !hasPrefix && !hasWww;

                if ((hasWww && !hasPrefix) || isDomainOnly) {
                    const link = `http://${field.fieldValue}`;

                    this.updateInput(ref, link);
                }
            }

            if (field && field.fieldValue && field.fieldType === 'PhoneNumber') {
                const newValue = field.fieldValue.replace(/\D+\./g, '');

                this.updateInput(ref, newValue);
            }

            if (field && field.fieldValue && (field.fieldType === 'DecimalNumber' || field.fieldType === 'Currency' || field.fieldType === 'Percent')) {
                const inputArray = field.fieldValue.toString().replace(/[^\d.]/g, '').split('');

                if (inputArray.slice(inputArray.indexOf('.')).length > 3) {
                    this.updateInput(ref, Number(field.fieldValue).toFixed(2));
                }
            }

            if (field && field.fieldValue && field.fieldType === 'DateTime') {
                field.fieldValue = this.formatDateTimeField(field.fieldValue, 'YYYY-MM-DD HH:mm:ss');
            }
        },

        formatDateTimeField(date, format = 'll') {
            const formattedDate = moment(date).isValid() ? date : new Date(date);

            return moment(formattedDate).format(format);
        },

        formatDateTimeFieldToTZ(date, format = 'll') {
            return moment.tz(date, this.serverTimeZone).format(format);
        },

        updateInput(ref, value) {
            if (this.$refs[ref] && this.$refs[ref][0]) {
                this.$refs[ref][0].input_updateInput(value);
                this.$refs[ref][0].input_updateValueExternal(value);
            }
        },

        manageCustomField() {
            window.open('/Admin/custom/manageCustomFields.jsp?table=Contact');
        },

        formatOptions(customField) {
            const allOptions = [...customField.options];
            const options = [];

            if (customField.fieldType === 'Radio' && customField.options.length <= 0) {
                this.yesNo.forEach((option) => {
                    options.push({ value: option.value.toString(), label: option.label });
                });
            } else if (customField.fieldType === 'DayOfWeek'
                        || customField.fieldType === 'Month'
                        || customField.fieldType === 'User') {
                allOptions.forEach((option) => {
                    options.push({ value: Number(option.id), label: option.label });
                });
            } else {
                allOptions.forEach((option) => {
                    options.push({ value: option.id, label: option.label });
                });
            }

            return options;
        },

        formatDrillDownOptions(customField) {
            const allOptions = [...customField.options];
            const resultOptions = [];

            allOptions.forEach((category) => {
                const categoryOptions = [];

                category.options.forEach((drillDown) => {
                    categoryOptions.push({ value: Number(drillDown.id), label: drillDown.label });
                });

                resultOptions.push({ label: category.label, options: categoryOptions });
            });

            return resultOptions;
        },

        formatYesNoOptions(customField) {
            return customField.fieldValue === null ? this.yesNoSelected : this.yesNo;
        },
    },
};
</script>

<style scoped>

</style>
